import React from 'react';
import { ButtonBack, ButtonNext, CarouselProvider, Slide, Slider } from 'pure-react-carousel';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { log } from 'limuirs-utils';
import { limuirsComponent } from 'limuirs-components';
import { imageType } from '../../types';

/**
 * Displays a sliding list of images that re-sizes itself based on viewable screen area.
 *
 * @author Dolan Halbrook
 */
class ImageSlider extends React.PureComponent {
  static propTypes = {
    /**
     * The image objects to display.
     */
    images: PropTypes.arrayOf(imageType).isRequired,
    /**
     * The size of the image to display.
     *
     * NOTE: this data will need to be present on the image object in the form of <size>_href.
     *
     * thumb = 150, small = 200, medium = 400, large = 999
     */
    imageSize: PropTypes.oneOf(['thumb', 'small', 'medium', 'large']),
    /**
     * The URL used by the image links.
     */
    href: PropTypes.string.isRequired,
    /**
     * The maximum number of images to display.
     */
    displayLimit: PropTypes.number,
    /**
     * The image height.
     */
    slideHeight: PropTypes.number,
    /**
     * The image width.
     */
    slideWidth: PropTypes.number,
    /**
     * The amount of whitespace between the images.
     */
    slidePadding: PropTypes.number
  };

  static defaultProps = {
    imageSize: 'thumb',
    displayLimit: 4,
    slideHeight: 100,
    slideWidth: 150,
    slidePadding: 10
  };

  static calculateWidthAndCount(windowWidth, effectiveSlideWidth, count) {
    let visibleSlideCount = count;
    let sliderWidth = effectiveSlideWidth * visibleSlideCount;
    while (visibleSlideCount > 1 && sliderWidth + effectiveSlideWidth > windowWidth) {
      /* eslint-disable no-plusplus */
      sliderWidth = effectiveSlideWidth * --visibleSlideCount;
    }
    return { sliderWidth, visibleSlideCount };
  }

  constructor(props) {
    super(props);
    const { displayLimit, images, slideWidth, slidePadding } = props;
    this.state = {
      visibleSlideCount: displayLimit,
      sliderWidth: (slideWidth + slidePadding) * Math.min(images.length, displayLimit)
    };
    this.handleWindowResize = _.debounce(this.handleWindowResize, 50).bind(this);
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleWindowResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleWindowResize);
  }

  handleWindowResize() {
    const { images, slideWidth, slidePadding, displayLimit } = this.props;
    const totalSlideCount = images.length;
    const { sliderWidth, visibleSlideCount } = ImageSlider.calculateWidthAndCount(
      window.innerWidth,
      slideWidth + slidePadding,
      Math.min(totalSlideCount, displayLimit)
    );
    this.setState({ sliderWidth, visibleSlideCount });
  }

  renderImageItem(image, index, link) {
    const { slideHeight, slideWidth, imageSize } = this.props;
    const imgStyle = { width: `${slideWidth}px`, height: `${slideHeight}px` };
    const sizeKey = `${imageSize}_href`;
    return (
      <Slide
        index={index}
        key={image.id}
        className="lia-image-slider-item"
        style={{ height: `${slideHeight}px` }}
      >
        <a href={link}>
          <img alt={image.title} src={`${image[sizeKey]}`} style={imgStyle} />
        </a>
      </Slide>
    );
  }

  render() {
    const { images, href, slideHeight, slideWidth, slidePadding } = this.props;
    const totalSlideCount = images.length;
    if (images && totalSlideCount > 0) {
      const { sliderWidth, visibleSlideCount } = this.state;
      const sliderStyle = {
        maxWidth: `${sliderWidth}px`,
        maxHeight: `${slideHeight}px`,
        marginLeft: `-${Math.floor(slidePadding / 2)}px`
      };
      return (
        <div className="lia-image-slider" style={sliderStyle}>
          <CarouselProvider
            naturalSlideHeight={slideHeight}
            naturalSlideWidth={slideWidth}
            step={visibleSlideCount}
            dragStep={visibleSlideCount}
            visibleSlides={visibleSlideCount}
            totalSlides={totalSlideCount}
          >
            <ButtonBack className="lia-image-slider-btn-back" />
            <Slider>
              {images.map((image, index) => this.renderImageItem(image, index, href))}
            </Slider>
            <ButtonNext className="lia-image-slider-btn-next" />
          </CarouselProvider>
        </div>
      );
    } else {
      log.debug(`No message images found`);
      return null;
    }
  }
}

export default limuirsComponent(module, ImageSlider);
